import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';

const parseHashParams = () => {
  const hash = (window.location.hash || '').replace(/^#/, '');
  const params = new URLSearchParams(hash);
  return {
    idToken: params.get('id_token') || '',
    state: params.get('state') || '',
    error: params.get('error') || '',
  };
};

const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    const normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + (4 - (normalized.length % 4 || 4)) % 4, '=');
    return JSON.parse(window.atob(padded));
  } catch (_) {
    return null;
  }
};

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogleToken } = useAuth();
  const { t } = useI18n();
  const [error, setError] = useState('');

  const isNativePath = window.location.pathname.includes('native');

  useEffect(() => {
    const { idToken, error: oauthError } = parseHashParams();

    // Native flow: the callback runs inside SFSafariViewController / Chrome Custom Tab,
    // which has its own localStorage, isolated from the main WKWebView. Instead of
    // authenticating here, relay the id_token back to the main app via the custom
    // URL scheme so the appUrlOpen listener in AuthContext can handle it.
    if (isNativePath) {
      if (oauthError) {
        setError(`OAuth error: ${oauthError}`);
        return;
      }
      if (!idToken) {
        setError(t('googleCallback.missingToken'));
        return;
      }
      window.location.replace(`poupix://auth/callback#id_token=${idToken}`);
      return;
    }

    const completeAuth = async () => {
      if (oauthError) {
        setError(t('googleCallback.oauthFailed', { error: oauthError }));
        return;
      }

      if (!idToken) {
        setError(t('googleCallback.missingToken'));
        return;
      }

      const expectedNonce = localStorage.getItem('pc_google_nonce') || '';
      const payload = decodeJwtPayload(idToken);
      if (expectedNonce && payload?.nonce !== expectedNonce) {
        setError(t('googleCallback.nonceFail'));
        return;
      }

      try {
        await loginWithGoogleToken(idToken);
        localStorage.removeItem('pc_google_nonce');
        navigate('/products');
      } catch (err) {
        const message = err?.response?.data?.detail || t('auth.googleFailed');
        setError(message);
      }
    };

    completeAuth();
  }, [isNativePath, loginWithGoogleToken, navigate, t]);

  if (error) {
    return (
      <div style={{ maxWidth: 640, margin: '48px auto', padding: 20 }}>
        <h2>{t('googleCallback.failedTitle')}</h2>
        <p>{error}</p>
        <Link to="/">{t('googleCallback.backHome')}</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '48px auto', padding: 20 }}>
      <h2>{t('googleCallback.signingIn')}</h2>
      <p>{t('googleCallback.wait')}</p>
    </div>
  );
};

export default GoogleAuthCallback;
